/**
 * Where each device's report is with the city (City Filing you can check,
 * piece 3). One line per device on the job page:
 *
 *   ✓ Filed with Saginaw Oct 17 → ✓ Delivered → ✓ City said received Oct 20
 *
 * with "Open email" to the Mailroom copy. Read-only. Hidden when the job has
 * no city filing to track (or the viewer can't see City Filing).
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { FilingView } from "@/pages/submissions/city-filing-loop";

export interface JobFilingDevice {
    assetId: string;
    submissionStatus: string;
    isPortal: boolean;
    providerName: string | null;
    filing: FilingView | null;
}

export type TrailChip = { text: string; tone: "good" | "warn" | "bad" | "mute" };

function clock(iso: string): string {
    return new Date(iso).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Chicago",
    });
}

function day(iso: string | null | undefined): string {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" });
}

/** Pure: the chips one device shows. Exported for tests. */
export function deviceTrail(d: JobFilingDevice): TrailChip[] {
    const city = d.providerName ?? "the city";
    const f = d.filing;
    if (!f) {
        if (d.submissionStatus === "submitted" || d.submissionStatus === "accepted")
            return [{ text: `✓ Filed with ${city}`, tone: "good" }];
        if (d.submissionStatus === "waived") return [{ text: "Not needed (waived)", tone: "mute" }];
        if (d.isPortal) return [{ text: `Not filed yet · file in the ${city} portal`, tone: "warn" }];
        return [{ text: `Not filed yet with ${city}`, tone: "warn" }];
    }
    if (f.kind === "proof")
        return [{ text: `✓ Filed with ${city} ${day(f.sentAt)} · proof in Mailroom`, tone: "good" }];
    if (f.status === "failed") return [{ text: `Email to ${city} did not send`, tone: "bad" }];
    const copy = f.sentVia === "gmail" ? " · copy in Mailroom Sent" : "";
    const out: TrailChip[] = [{ text: `✓ Sent to ${city} ${day(f.sentAt)} ${clock(f.sentAt)}${copy}`, tone: "good" }];
    if (f.trail.state === "bounced") {
        out.push({ text: `✗ Bounced: ${f.delivery?.reason ?? "did not arrive"}`, tone: "bad" });
        return out;
    }
    out.push(
        f.delivery?.status === "delivered" ? { text: "✓ Delivered", tone: "good" } : { text: "Sending", tone: "mute" },
    );
    if (f.confirmedAt) out.push({ text: `✓ City said received ${day(f.reply?.at ?? f.confirmedAt)}`, tone: "good" });
    else if (f.trail.state === "needs_person") out.push({ text: "City wrote back: needs a person", tone: "warn" });
    else if (f.trail.state === "filed_no_reply") out.push({ text: "✓ Filed, no reply", tone: "good" });
    else if (f.trail.days >= 7) out.push({ text: "Waiting on city · no answer in 7 days", tone: "warn" });
    else out.push({ text: `Waiting on city · day ${f.trail.days} of 7`, tone: "warn" });
    return out;
}

const TONE: Record<TrailChip["tone"], string> = {
    good: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    warn: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
    bad: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
    mute: "bg-muted text-muted-foreground",
};

export function CityFilingTrail({ jobId, serials }: { jobId: string; serials: Map<string, string> }) {
    const q = useQuery({
        queryKey: ["city-filings", "by-job", jobId],
        queryFn: () => api.get<{ devices: JobFilingDevice[] }>(`/api/city-filings/by-job/${jobId}`),
        retry: false,
    });
    const devices = q.data?.devices ?? [];
    if (devices.length === 0) return null;
    return (
        <div className="rounded-xl border border-border bg-card p-3" data-testid="city-filing-trail">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Filed with the city
            </div>
            <ul className="flex flex-col divide-y divide-border">
                {devices.map((d) => {
                    const thread = d.filing?.mailThreadId ?? d.filing?.reply?.threadId ?? d.filing?.proofThreadId;
                    return (
                        <li key={d.assetId} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 py-2">
                            <div className="min-w-0">
                                <b className="text-sm">SN {serials.get(d.assetId) ?? "—"}</b>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                                    {deviceTrail(d).map((c, i) => (
                                        <span key={i} className="inline-flex items-center gap-1.5">
                                            {i > 0 && <span className="text-muted-foreground">→</span>}
                                            <span className={`rounded-full px-2 py-0.5 font-semibold ${TONE[c.tone]}`}>
                                                {c.text}
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                            {thread && (
                                <Link
                                    to={`/mailroom?thread=${thread}`}
                                    className="text-xs font-semibold text-indigo-600 underline"
                                >
                                    Open email
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
